import React, { createContext, useContext, useState } from "react";
import type { NewPatientStep1FormValues } from "../step1/page";
import type { SearchPatientsForRegistrationQuery } from "../step2/__generated__/page.gql";
import type { NewPatientStep2FormValues } from "../step2/page";
import type { PakistanFormValues } from "./pakistan/PakistanNewPatientSubmitter";

export type RegistrationFormStateContextType = {
  step1FormValues: NewPatientStep1FormValues | null;
  step2FormValues: NewPatientStep2FormValues | null;
  duplicates: SearchPatientsForRegistrationQuery["searchPatients"] | null;
  nextLinkOverride: string | null;
  // Note: This can be removed once PK form alignment has been fully rolled out
  pakistanFormValues: PakistanFormValues | null;

  setStep1FormValues: (values: NewPatientStep1FormValues) => void;
  setStep2FormValues: (values: NewPatientStep2FormValues) => void;
  setDuplicates: (
    duplicates: SearchPatientsForRegistrationQuery["searchPatients"],
    nextLinkOverride?: string,
  ) => void;
  setPakistanFormValues: (values: PakistanFormValues | null) => void;

  resetFormState: () => void;

  trackingId: string | null;
  setTrackingId: (value: string | null) => void;
};

export const RegistrationFormStateContext = createContext<
  RegistrationFormStateContextType | undefined
>(undefined);

export const RegistrationFormStateProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [trackingId, setTrackingId] = useState<string | null>(null);

  const [step1FormValues, setStep1FormValuesState] =
    useState<NewPatientStep1FormValues | null>(null);
  const [step2FormValues, setStep2FormValuesState] =
    useState<NewPatientStep2FormValues | null>(null);
  const [duplicates, setDuplicatesState] = useState<
    SearchPatientsForRegistrationQuery["searchPatients"] | null
  >(null);
  const [nextLinkOverride, setNextLinkOverrideState] = useState<string | null>(
    null,
  );
  const [pakistanFormValues, setPakistanFormValuesState] =
    useState<PakistanFormValues | null>(null);

  const setStep1FormValues = (values: NewPatientStep1FormValues) => {
    setStep1FormValuesState(values);
  };

  const setStep2FormValues = (values: NewPatientStep2FormValues) => {
    setStep2FormValuesState(values);
  };

  const setDuplicates = (
    duplicates: SearchPatientsForRegistrationQuery["searchPatients"],
    nextLinkOverride?: string,
  ) => {
    setDuplicatesState(duplicates);
    setNextLinkOverrideState(nextLinkOverride || null);
  };

  const setPakistanFormValues = (values: PakistanFormValues | null) => {
    setPakistanFormValuesState(values);
  };

  const resetFormState = () => {
    setStep1FormValuesState(null);
    setStep2FormValuesState(null);
    setDuplicatesState(null);
    setNextLinkOverrideState(null);
    setPakistanFormValuesState(null);
  };

  return (
    <RegistrationFormStateContext.Provider
      value={{
        step1FormValues,
        step2FormValues,
        duplicates,
        nextLinkOverride,
        pakistanFormValues,
        setStep1FormValues,
        setStep2FormValues,
        setDuplicates,
        setPakistanFormValues,
        resetFormState,
        trackingId,
        setTrackingId,
      }}
    >
      {children}
    </RegistrationFormStateContext.Provider>
  );
};

export const useRegistrationFormState = () => {
  const context = useContext(RegistrationFormStateContext);
  if (context === undefined) {
    throw new Error(
      "useRegistrationFormState must be used within a RegistrationFormStateProvider",
    );
  }
  return context;
};
